import React, { useState } from 'react';
import { Loader2, Undo2, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { ApiMethod } from '../../../common/models/api-types.type';
import { routes } from '../../../assets/constants';
import { requestPageOutline } from './page-outline';

/** The settings a suggestion may set — and so the ones Undo puts back. */
const SUGGESTED_KEYS = [
    'containerSelector', 'insertPosition', 'insertIndex', 'aspectRatio',
    'minHeight', 'panelCSS', 'containerBehavior',
] as const;

interface Suggestion {
    containerSelector: string;
    insertPosition: string;
    insertIndex?: number;
    aspectRatio?: string;
    minHeight?: number;
    panelCSS?: string;
    containerBehavior?: string;
    explanation: string;
}

type Phase =
    | { kind: 'idle' }
    | { kind: 'working' }
    | { kind: 'suggested'; suggestion: Suggestion }
    | { kind: 'applied'; previous: Record<string, any> }
    | { kind: 'error'; message: string };

/**
 * "Describe where it goes."
 *
 * Pointing at an element answers before, after or inside it. A vendor thinks
 * in "the second row of the gallery, full width" — so they can say that, and a
 * model reading an outline of their page fills in the placement fields below.
 * The suggestion is shown before anything changes, applying it is one click
 * that the preview then draws, and Undo puts back exactly what it replaced.
 * Nothing here can do more than the form itself: it fills the same fields.
 */
export const PlacementAssistant = ({ settings, onChange, t }) => {
    const { sendAuthGuardedRequest } = useAuthContext();
    const k = (suffix: string) => t(`INTEGRATION.FEATURES.VISUALIZER.INLINE.${suffix}`);
    const [description, setDescription] = useState('');
    const [phase, setPhase] = useState<Phase>({ kind: 'idle' });

    const positionLabel = (position: string) => ({
        before: k('POSITION_BEFORE'),
        after: k('POSITION_AFTER'),
        prepend: k('POSITION_PREPEND'),
        append: k('POSITION_APPEND'),
        index: k('POSITION_INDEX'),
    }[position] || position);

    const ratioLabel = (ratio: string) =>
        ratio === 'auto' ? k('RATIO_AUTO') : ratio === 'match' ? k('RATIO_MATCH') : ratio;

    const suggest = async () => {
        const text = description.trim();
        if (!text) return;
        setPhase({ kind: 'working' });
        const outline = await requestPageOutline();
        if (!outline.nodes.length) {
            setPhase({ kind: 'error', message: k(outline.missing === 'no-answer' ? 'AI_OLD_SCRIPT' : 'AI_NO_PAGE') });
            return;
        }
        try {
            const current: Record<string, any> = {};
            SUGGESTED_KEYS.forEach((key) => { if (settings?.[key] !== undefined) current[key] = settings[key]; });
            const suggestion = await sendAuthGuardedRequest(
                ApiMethod.POST,
                routes.integration.placementSuggestion,
                { description: text, outline: outline.nodes, viewport: outline.viewport, current },
            ) as Suggestion;
            setPhase({ kind: 'suggested', suggestion });
        } catch (error: any) {
            // 422 is the one answer about the page: the model pointed at nothing
            // on it. Anything else — the provider down, a key refused — is not
            // the vendor's description, and must not tell them to reword it.
            setPhase({ kind: 'error', message: k(error?.status === 422 ? 'AI_FAILED' : 'AI_UNAVAILABLE') });
        }
    };

    const apply = (suggestion: Suggestion) => {
        const previous: Record<string, any> = {};
        const next: Record<string, any> = {};
        SUGGESTED_KEYS.forEach((key) => {
            previous[key] = settings?.[key];
            // A field the suggestion leaves out keeps its value, except the item
            // number, which means nothing once the position is not "as item N".
            if (suggestion[key] !== undefined) next[key] = suggestion[key];
        });
        if (suggestion.insertPosition !== 'index') next.insertIndex = undefined;
        onChange(next);
        setPhase({ kind: 'applied', previous });
    };

    const undo = (previous: Record<string, any>) => {
        onChange(previous);
        setPhase({ kind: 'idle' });
    };

    const working = phase.kind === 'working';

    return (
        <div className="flex flex-col gap-3" data-testid="placement-assistant">
            <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-foreground">{k('AI_TITLE')}</span>
                <span className="text-xs text-muted-foreground">{k('AI_HINT')}</span>
            </div>
            <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void suggest();
                }}
                placeholder={k('AI_PLACEHOLDER')}
                maxLength={500}
                rows={2}
                disabled={working}
                data-testid="placement-assistant-input"
            />
            <div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void suggest()}
                    disabled={working || !description.trim()}
                    data-testid="placement-assistant-suggest"
                >
                    {working ? <Loader2 className="size-4 animate-spin" /> : <Wand2 className="size-4" />}
                    {working ? k('AI_WORKING') : k('AI_SUGGEST')}
                </Button>
            </div>

            {phase.kind === 'error' && (
                <p className="text-xs text-destructive" data-testid="placement-assistant-error">{phase.message}</p>
            )}

            {phase.kind === 'suggested' && (
                <div className="flex flex-col gap-3 rounded-lg border p-3" data-testid="placement-assistant-suggestion">
                    {phase.suggestion.explanation && (
                        <p className="text-sm text-foreground">{phase.suggestion.explanation}</p>
                    )}
                    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                        <dt className="text-muted-foreground">{k('AI_ELEMENT')}</dt>
                        <dd className="truncate font-mono text-foreground">{phase.suggestion.containerSelector}</dd>
                        <dt className="text-muted-foreground">{k('POSITION')}</dt>
                        <dd className="text-foreground">
                            {positionLabel(phase.suggestion.insertPosition)}
                            {phase.suggestion.insertPosition === 'index' ? ` · ${phase.suggestion.insertIndex}` : ''}
                        </dd>
                        {phase.suggestion.aspectRatio && (
                            <>
                                <dt className="text-muted-foreground">{k('RATIO')}</dt>
                                <dd className="text-foreground">{ratioLabel(phase.suggestion.aspectRatio)}</dd>
                            </>
                        )}
                        {phase.suggestion.minHeight ? (
                            <>
                                <dt className="text-muted-foreground">{k('MIN_HEIGHT')}</dt>
                                <dd className="text-foreground">{phase.suggestion.minHeight}</dd>
                            </>
                        ) : null}
                        {phase.suggestion.panelCSS && (
                            <>
                                <dt className="text-muted-foreground">{k('CSS')}</dt>
                                <dd className="break-all font-mono text-foreground">{phase.suggestion.panelCSS}</dd>
                            </>
                        )}
                    </dl>
                    <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => apply(phase.suggestion)} data-testid="placement-assistant-apply">
                            {k('AI_APPLY')}
                        </Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setPhase({ kind: 'idle' })}>
                            {k('AI_DISMISS')}
                        </Button>
                    </div>
                </div>
            )}

            {phase.kind === 'applied' && (
                <div className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs" data-testid="placement-assistant-applied">
                    <span className="text-muted-foreground">{k('AI_APPLIED')}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => undo(phase.previous)} data-testid="placement-assistant-undo">
                        <Undo2 className="size-4" />
                        {k('AI_UNDO')}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default PlacementAssistant;
